import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/api';
import { showError, showSuccess } from '../../utils/sweetAlert';
import SEO from '../shared/SEO';

const INITIAL_FORM = {
  name: '',
  company: '',
  whatsapp: '',
  email: '',
  product: '',
  quantity: '',
  comment: ''
};

const ALLOWED_LOGO_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_LOGO_SIZE = 10 * 1024 * 1024;

const MerchandisingPage = () => {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [logo, setLogo] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = event => {
    const { name, value } = event.target;
    setFormData(previous => ({ ...previous, [name]: value }));
  };

  const handleLogoChange = event => {
    const file = event.target.files[0];
    if (!file) {
      setLogo(null);
      return;
    }
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      event.target.value = '';
      setLogo(null);
      showError('El logo debe ser JPG, PNG, WebP o GIF');
      return;
    }
    if (file.size > MAX_LOGO_SIZE) {
      event.target.value = '';
      setLogo(null);
      showError('El logo no puede superar los 10 MB');
      return;
    }
    setLogo(file);
  };

  const handleSubmit = async event => {
    event.preventDefault();
    const form = event.currentTarget;
    setSubmitting(true);

    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([key, value]) => payload.append(key, value.trim()));
      if (logo) payload.append('logo', logo);

      const response = await fetch(API_ENDPOINTS.merchandising.requestQuote(), {
        method: 'POST',
        body: payload
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'No pudimos enviar la solicitud');

      setFormData(INITIAL_FORM);
      setLogo(null);
      form.reset();
      showSuccess('¡Solicitud enviada! Te contactaremos para preparar el presupuesto.');
    } catch (error) {
      showError(error.message || 'No pudimos enviar la solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Merchandising para Empresas, Equipos y Eventos"
        description="Solicitá productos e indumentaria personalizada para tu empresa, emprendimiento, equipo, colegio o evento."
        url="/merchandising"
        type="website"
        breadcrumbs={[
          { name: 'Inicio', url: '/' },
          { name: 'Personalizados', url: '/personalizados' },
          { name: 'Merchandising', url: '/merchandising' }
        ]}
      />

      <main className="merchandising-page">
        <section className="merchandising-hero">
          <div className="personalizados-shell merchandising-hero-grid">
            <div>
              <Link to="/personalizados" className="merchandising-back-link"><i className="fa-solid fa-arrow-left"></i> Personalizados</Link>
              <span className="personalizados-kicker"><i className="fa-solid fa-building"></i> Soluciones para grupos</span>
              <h1>Merchandising para tu marca</h1>
              <p>Tenemos una propuesta para vos. Personalizamos una amplia gama de productos con impresión sobre textiles y superficies rígidas, adaptada a tu empresa, emprendimiento, equipo, colegio o evento.</p>
              <div className="merchandising-benefits">
                <span><i className="fa-solid fa-check"></i> Atención personalizada</span>
                <span><i className="fa-solid fa-check"></i> Pedidos por cantidad</span>
                <span><i className="fa-solid fa-check"></i> Textiles y superficies rígidas</span>
              </div>
            </div>
            <div className="merchandising-hero-visual" aria-hidden="true">
              <img src="/images/customization/hoodie-front-neutral.webp" alt="" />
              <div><i className="fa-solid fa-pen-ruler"></i><span>Tu marca<br /><strong>en productos reales</strong></span></div>
            </div>
          </div>
        </section>

        <section className="merchandising-form-section">
          <div className="personalizados-shell merchandising-content-grid">
            <aside className="merchandising-form-intro">
              <span>Presupuesto a medida</span>
              <h2>Hablemos de tu proyecto</h2>
              <p>Completá los datos principales. Vamos a revisar tu consulta y contactarte para definir productos, técnicas y cantidades.</p>
              <div className="merchandising-process">
                <div><strong>1</strong><span><b>Contanos tu idea</b><small>Producto, cantidad y objetivo.</small></span></div>
                <div><strong>2</strong><span><b>Revisamos el pedido</b><small>Evaluamos materiales y personalización.</small></span></div>
                <div><strong>3</strong><span><b>Recibís la propuesta</b><small>Te contactamos con el presupuesto.</small></span></div>
              </div>
            </aside>

            <div className="merchandising-form-card">
              <form onSubmit={handleSubmit} className="merchandising-form">
                <div className="merchandising-form-grid">
                  <label>
                    <span>Nombre *</span>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} autoComplete="name" maxLength="120" required />
                  </label>
                  <label>
                    <span>Empresa *</span>
                    <input type="text" name="company" value={formData.company} onChange={handleChange} autoComplete="organization" maxLength="160" required />
                  </label>
                  <label>
                    <span>WhatsApp *</span>
                    <input type="tel" name="whatsapp" value={formData.whatsapp} onChange={handleChange} autoComplete="tel" placeholder="+598 99 123 456" maxLength="40" required />
                  </label>
                  <label>
                    <span>Email *</span>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} autoComplete="email" maxLength="254" required />
                  </label>
                  <label>
                    <span>Producto que necesitás *</span>
                    <input type="text" name="product" value={formData.product} onChange={handleChange} placeholder="Ej: remeras, gorras, lanyards" maxLength="180" required />
                  </label>
                  <label>
                    <span>Cantidad aproximada *</span>
                    <input type="number" name="quantity" value={formData.quantity} onChange={handleChange} min="1" max="100000" inputMode="numeric" required />
                  </label>
                </div>

                <label>
                  <span>Comentario</span>
                  <textarea name="comment" value={formData.comment} onChange={handleChange} rows="5" maxLength="2000" placeholder="Contanos colores, fecha estimada, tipo de evento o cualquier detalle útil."></textarea>
                </label>

                <label className="merchandising-upload">
                  <input type="file" name="logo" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleLogoChange} />
                  <span className="merchandising-upload-icon"><i className="fa-solid fa-cloud-arrow-up"></i></span>
                  <span><strong>{logo ? logo.name : 'Adjuntá tu logo (opcional)'}</strong><small>JPG, PNG, WebP o GIF · máximo 10 MB</small></span>
                </label>

                <button type="submit" className="merchandising-submit" disabled={submitting}>
                  {submitting ? <><i className="fa-solid fa-spinner fa-spin"></i> Enviando solicitud...</> : <><i className="fa-solid fa-paper-plane"></i> Solicitar presupuesto</>}
                </button>
                <p className="merchandising-privacy"><i className="fa-solid fa-lock"></i> Usaremos estos datos únicamente para responder tu consulta.</p>
              </form>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default MerchandisingPage;
